import { FC } from 'react';
import type { GetServerSideProps } from 'next';
import Metatags from 'components/Metatags/Metatags';
import ElectionHub2026 from 'endpoints/ElectionHub2026/ElectionHub2026';
import {
  parseGetReadyToVoteItems,
  GetReadyToVoteItem,
} from 'endpoints/ElectionHub2026/utils/getReadyToVoteBoxes';
import {
  getYoutubePlaylistVideos,
  YoutubePlaylistVideo,
} from 'utils/getYoutubePlaylistVideos';
import { ELECTION_VIDEOS_PLAYLIST_ID } from 'utils/constants';
import AlertWrapper from 'components/Alert/AlertWrapper';
import fetcher from 'utils/fetcher';
import ELECTIONVIDEOQUERY from 'endpoints/ElectionHub2026/electionvideo.gql';
import CQUERY from 'endpoints/Collection/col.gql';
import getLiveVideoId from 'utils/getLiveVideoId';
import '/public/css/pages/electionHub2026.css';
import query from '../endpoints/ElectionHub2024/electionhubquery.gql';

interface Props {
  data: any;
  videos: YoutubePlaylistVideo[];
  getReadyToVoteItems: GetReadyToVoteItem[];
  membershipMessage: GetReadyToVoteItem | null;
  liveVideoId: string | null;
  electionCoverage: any;
  politicsFriday: any;
  // layout is consumed by _app.js to pick the page layout, not by this component.
  // eslint-disable-next-line react/no-unused-prop-types
  layout: string;
}

const ElectionHub2026Page: FC<Props> = ({
  videos,
  getReadyToVoteItems,
  membershipMessage,
  liveVideoId,
  data,
  electionCoverage,
  politicsFriday,
}) => {
  return (
    <>
      <Metatags
        title="MPR News 2026 Election Hub: Minnesota Voting Resources, Election News, and More"
        description="MPR News presents election news and resources for voters across Minnesota. Get ready to vote in the upcoming election, understand the candidates running in local elections, and get the latest news."
        topic="electionhub"
        image="https://img.apmcdn.org/894884e5f5c132c567ab51d36ac73648c1e8d690/widescreen/deaf8a-20260626-a-person-votes-2000.jpg"
        imageWidth={2000}
        imageHeight={1123}
        imageAlt="Voter fills out ballot."
      />
      <AlertWrapper resourceType="page" />
      <div className="page-purpose" data-mpr-content-topic="electionhub">
        <div className="contentGrid_noSidebar contentGrid">
          <div className="contentGrid_main election-hub">
            <ElectionHub2026
              data={data}
              videos={videos}
              getReadyToVoteItems={getReadyToVoteItems}
              membershipMessage={membershipMessage}
              liveVideoId={liveVideoId}
              electionCoverage={electionCoverage}
              politicsFriday={politicsFriday}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export const getServerSideProps = (async ({ res }) => {
  res.setHeader('Cache-Control', 'public, max-age=300');
  // Youtube API hard limits us daily so important to keep a slightly longer cache on the Playlist videos
  const [videos, electionvideo, electionCoverage, politicsFriday] =
    await Promise.all([
      getYoutubePlaylistVideos(ELECTION_VIDEOS_PLAYLIST_ID),
      fetcher(ELECTIONVIDEOQUERY),
      fetcher(CQUERY, {
        contentAreaSlug: process.env.CONTENT_AREA_SLUG,
        slug: 'politics/election-2026',
      }),
      fetcher(CQUERY, {
        contentAreaSlug: process.env.CONTENT_AREA_SLUG,
        slug: 'politics/mn-politics/election-politics-friday',
      }),
    ]);

  const liveVideoId = getLiveVideoId(electionvideo?.electionvideo);

  const data = await fetcher(query, {
    contentAreaSlug: process.env.CONTENT_AREA_SLUG,
    slug: 'minnesota-voter-guide',
  });

  const getReadyToVoteItems = parseGetReadyToVoteItems(
    data?.getReadyToVote?.json
  );

  const membershipMessage =
    getReadyToVoteItems.length === 5 ? getReadyToVoteItems.pop() ?? null : null;

  return {
    props: {
      data,
      videos,
      getReadyToVoteItems,
      membershipMessage,
      liveVideoId,
      electionCoverage,
      politicsFriday,
      layout: 'election',
    },
  };
}) satisfies GetServerSideProps<Props>;

export default ElectionHub2026Page;
